import { useEffect, useRef, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  downloadProspectsImportTemplate,
  importProspects,
  type ProspectImportResult,
} from "@/lib/prospectsApi";
import { AlertCircle, Download, FileText, Upload, X } from "lucide-react";

type ImportProspectsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: () => void;
};

const ImportProspectsDialog = ({ open, onOpenChange, onImported }: ImportProspectsDialogProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<ProspectImportResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { userRole } = useAuth();
  const { toast } = useToast();

  const canImport = userRole === "ceo" || userRole === "general_manager" || userRole === "sales_staff";

  useEffect(() => {
    if (!open) {
      setFile(null);
      setResults(null);
      setProgress(0);
      setImporting(false);
      setDownloadingTemplate(false);
      setIsDragging(false);
    }
  }, [open]);

  const validateAndSetFile = (selectedFile: File | null | undefined) => {
    if (!selectedFile) return;

    const isXlsx = selectedFile.name.toLowerCase().endsWith(".xlsx");
    if (!isXlsx) {
      toast({
        title: "Invalid file",
        description: "Please select a valid XLSX file.",
        variant: "destructive",
      });
      return;
    }

    setFile(selectedFile);
    setResults(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateAndSetFile(e.target.files?.[0]);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    validateAndSetFile(event.dataTransfer.files?.[0]);
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setProgress(20);
    setResults(null);

    try {
      const result = await importProspects(file);
      setProgress(100);
      setResults(result);
      onImported?.();

      if (result.errors > 0) {
        toast({
          title: "Import completed with issues",
          description: `${result.created} created, ${result.updated} updated, ${result.errors} row error(s).`,
          variant: "destructive",
        });
        return;
      }

      if (result.created === 0 && result.updated === 0 && result.duplicates_skipped > 0) {
        toast({
          title: "No new prospects imported",
          description: `${result.duplicates_skipped} duplicate row(s) were skipped.`,
        });
        return;
      }

      toast({
        title: "Import completed",
        description: `${result.created} created, ${result.updated} updated.`,
      });
    } catch (error: any) {
      toast({
        title: "Import failed",
        description: error?.message ?? "Failed to import file.",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const blob = await downloadProspectsImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "prospects-import-template.xlsx";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error: any) {
      toast({
        title: "Template download failed",
        description: error?.message ?? "Could not download template.",
        variant: "destructive",
      });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import Prospects</DialogTitle>
          <DialogDescription>
            Upload an XLSX file to bulk import prospect companies and contacts.
          </DialogDescription>
        </DialogHeader>

        {!canImport ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You don't have permission to import prospects.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Columns expected:
                {" "}
                <strong>CompanyName, Country, Address, BusinessType, ClientType, Contact Person, Position, Email, Telephone, ReferredFrom</strong>.
                {" "}
                `BusinessType` accepts `operator`, `agency`, `final_client` (also values like `Final Client`).
                {" "}
                `ClientType` accepts one or more values separated by `;`, `,`, `/`, `|` or line breaks.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>XLSX file</Label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                  file
                    ? "border-primary bg-primary/5"
                    : isDragging
                      ? "border-primary bg-primary/10"
                      : "border-muted-foreground/25 hover:border-muted-foreground/50"
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragging(true);
                }}
                onDragEnter={() => setIsDragging(true)}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
              >
                <Input
                  ref={fileInputRef}
                  id="prospects-import-file"
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileChange}
                  disabled={importing}
                  className="hidden"
                />
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="h-8 w-8 text-primary" />
                    <div className="text-left">
                      <p className="font-medium text-sm">{file.name}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(event) => {
                        event.stopPropagation();
                        setFile(null);
                        setResults(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="h-10 w-10 mx-auto text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Clique para selecionar ou arraste o arquivo</p>
                      <p className="text-xs text-muted-foreground">Apenas .xlsx</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {importing && (
              <div className="space-y-2">
                <Progress value={progress} />
                <p className="text-sm text-muted-foreground text-center">
                  Importing... {Math.round(progress)}%
                </p>
              </div>
            )}

            {results && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    <p><strong>Import Results:</strong></p>
                    <p>Created: {results.created}</p>
                    <p>Updated: {results.updated}</p>
                    <p>Contacts added: {results.contacts_added}</p>
                    <p>Duplicates skipped: {results.duplicates_skipped}</p>
                    <p>Errors: {results.errors}</p>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={handleDownloadTemplate}
            disabled={!canImport || importing || downloadingTemplate}
          >
            <Download className="mr-2 h-4 w-4" />
            {downloadingTemplate ? "Downloading..." : "Download template"}
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={importing}>
              Close
            </Button>
            <Button
              type="button"
              onClick={handleImport}
              disabled={!canImport || !file || importing}
            >
              <Upload className="mr-2 h-4 w-4" />
              {importing ? "Importing..." : "Import prospects"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportProspectsDialog;
